/**
 * The files the AGENT can send a prospect: PDFs, images and videos.
 *
 * One catalogue for the browser and the server, on the `TEMPLATE_MEDIA_LIMITS`
 * precedent: the CRM form, the upload validator and the multipart parser must
 * enforce the same number, and before this the CRM kept its own copy of the
 * PDF cap next to an env var the server read — two truths that only agreed by
 * coincidence.
 *
 * The kind is DERIVED from the MIME type and never stored beside it. WhatsApp
 * decides how a file renders from the message type (`image` / `video` /
 * `document`), so the kind is what the transport needs — but a `media_kind`
 * column next to `mime_type` would be a second answer to one question.
 */
export const AGENT_MEDIA_KINDS = ["document", "image", "video"];
export const AGENT_MEDIA_LIMITS = {
    document: {
        mimeTypes: ["application/pdf"],
        // Ours, not Meta's (which allows 100): the send path buffers the whole file
        // in memory. Unchanged from when this was `DOCUMENTS_MAX_FILE_SIZE_MB`.
        maxSizeMb: 10,
        accept: "application/pdf",
        label: "Documento",
        icon: "📄",
        markerLabel: "Documento",
    },
    image: {
        // Meta's allowlist for `image` messages. WebP is deliberately absent even
        // though logos accept it: Graph treats it as the sticker format, so an
        // admin could upload one that only fails when sent to a real prospect.
        mimeTypes: ["image/jpeg", "image/jpg", "image/png"],
        // Meta's own ceiling for images; raising ours only moves the rejection later.
        maxSizeMb: 5,
        accept: "image/jpeg,image/png",
        label: "Imagen",
        icon: "🖼️",
        markerLabel: "Imagen",
    },
    video: {
        // MP4 is the only container both transports deliver as an inline player.
        // The CODEC is checked too (H.264 + AAC, see `lib/file-validation.ts` in the
        // backend): an iPhone records HEVC by default, and Meta accepts the upload
        // and fails the delivery later, where nobody sees it.
        mimeTypes: ["video/mp4"],
        // Meta's cap for `video`, and the same memory argument as the PDF.
        maxSizeMb: 16,
        accept: "video/mp4",
        label: "Video",
        icon: "🎬",
        markerLabel: "Video",
    },
};
/** Every accepted type, for one `accept` attribute. */
export const AGENT_MEDIA_ACCEPT = AGENT_MEDIA_KINDS.map((k) => AGENT_MEDIA_LIMITS[k].accept).join(",");
/** The largest per-kind cap: the multipart parser's outer ceiling. */
export const AGENT_MEDIA_MAX_SIZE_MB = Math.max(...AGENT_MEDIA_KINDS.map((k) => AGENT_MEDIA_LIMITS[k].maxSizeMb));
/** Which kind a MIME type belongs to, or null when the agent does not send it. */
export function mediaKindForMimeType(mimeType) {
    for (const kind of AGENT_MEDIA_KINDS) {
        if (AGENT_MEDIA_LIMITS[kind].mimeTypes.includes(mimeType))
            return kind;
    }
    return null;
}
// ----- Product media (docs/plans/product-media.md) -----
/**
 * A product's media SLOTS: at most one of each. A gallery holds 1–10 photos,
 * a video or a pdf exactly one file.
 */
export const PRODUCT_MEDIA_KINDS = ["gallery", "video", "pdf"];
/**
 * What file each slot accepts — which is also which `AGENT_MEDIA_LIMITS` entry
 * (allowlist, cap, codec check) validates it. One catalogue for documents and
 * product media, so a slot can never accept what a document would refuse.
 */
export const PRODUCT_MEDIA_FILE_KIND = {
    gallery: "image",
    video: "video",
    pdf: "document",
};
/** A product rule, not a schema constraint: raising it needs no migration. */
export const PRODUCT_GALLERY_MAX_FILES = 10;
/** Same as a document's description, so the migration never truncates one. */
export const PRODUCT_MEDIA_DESCRIPTION_MAX = 500;
export const AGENT_MATERIAL_KINDS = [...AGENT_MEDIA_KINDS, "gallery"];
export function materialKindForSlot(kind) {
    return kind === "gallery" ? "gallery" : PRODUCT_MEDIA_FILE_KIND[kind];
}
/** Spanish label, icon and history-marker label for every kind the agent sends. */
export const AGENT_MATERIAL_DISPLAY = {
    document: AGENT_MEDIA_LIMITS.document,
    image: AGENT_MEDIA_LIMITS.image,
    video: AGENT_MEDIA_LIMITS.video,
    gallery: { label: "Galería", icon: "🖼️", markerLabel: "Galería" },
};
/**
 * The row persisted in `messages` after the agent sends a file, e.g.
 * `[Imagen: Instalaciones]` — or one row for a whole gallery,
 * `[Galería: Diseño de Modas]`, never one per photo.
 *
 * One builder because the marker has three readers besides its writer: the
 * WhatsApp send site dedupes on it, the prompt tells the model not to re-send
 * anything shown with it, and the CRM picks the timeline icon from it. A prompt
 * rule that only names `[Documento: ...]` would not recognise an image it
 * already sent.
 */
export function buildMediaMarker(kind, title) {
    return `[${AGENT_MATERIAL_DISPLAY[kind].markerLabel}: ${title}]`;
}
/** The kind a persisted marker names, or null for any other content. */
export function mediaKindForMarker(content) {
    for (const kind of AGENT_MATERIAL_KINDS) {
        if (content.startsWith(`[${AGENT_MATERIAL_DISPLAY[kind].markerLabel}`))
            return kind;
    }
    return null;
}
//# sourceMappingURL=agent-media.js.map